import { useTenantContext } from '@trashlab/sitesapp/src/contexts/use-tenant-context';
import { trpcPricingListSkusOpen } from '@trashlab/api/src/generated/hooks';
import { RedebinsDumpsterCard } from '../dumpster-rentals/dumpster-card';
import { Link } from '@trashlab/sitesapp/src/components/shared/link';

export const DumpsterOptions = () => {
  const { website } = useTenantContext();
  const tenantId = website?.organization?.id;

  const { data: skus, isLoading } = trpcPricingListSkusOpen.useQuery(
    {
      tenantId: tenantId ?? '',
      onlineOrderEnabled: true,
      status: 'ACTIVE',
      defaultOnly: true,
    },
    {
      enabled: !!tenantId,
    },
  );

  // Sort by cubic yardage (larger first)
  const sortedSkus = skus
    ? [...skus].sort((a, b) => {
        const aYardsRaw = a.product?.cubicYardage;
        const bYardsRaw = b.product?.cubicYardage;

        const aYards =
          typeof aYardsRaw === 'string'
            ? parseInt(aYardsRaw.replace(/\D/g, ''), 10) || 0
            : Number(aYardsRaw) || 0;

        const bYards =
          typeof bYardsRaw === 'string'
            ? parseInt(bYardsRaw.replace(/\D/g, ''), 10) || 0
            : Number(bYardsRaw) || 0;

        return bYards - aYards;
      })
    : [];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            <em>Commercial Dumpster Options</em>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Whether it&apos;s a demo, renovation, or commercial construction project,
            our dumpsters handle everything from lumber and roofing to old flooring
            and windows. Perfect for contractors and businesses.
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E31837]" />
          </div>
        )}

        {/* No Skus State */}
        {!isLoading && (!skus || skus.length === 0) && (
          <div className="text-center">
            <p className="text-gray-600 mb-6">Please contact us for availability.</p>
            <Link
              href="/contact"
              className="inline-block bg-[#E31837] hover:bg-[#c41530] text-white font-bold py-3 px-6 rounded transition-colors"
            >
              Contact Us
            </Link>
          </div>
        )}

        {/* Dumpster Grid */}
        {!isLoading && sortedSkus.length > 0 && (
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {sortedSkus.map((sku) => (
              <RedebinsDumpsterCard key={sku.id} sku={sku} />
            ))}
          </div>
        )}

        {/* Commercial Info */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-white rounded-lg p-8 shadow-md border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Why Contractors Choose RED-E-BIN
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-[#E31837] mb-2">
                  Construction & Demolition
                </h4>
                <p className="text-gray-600">
                  Handle all your project debris—lumber, roofing, siding, flooring,
                  windows, and more. Our dumpsters are built for the tough stuff.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-[#E31837] mb-2">Mixed Solid Waste</h4>
                <p className="text-gray-600">
                  From packaging to furniture and general job site waste, we handle
                  it all. Additional fees may apply for items like mattresses.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Need Help Section */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-2">Questions about commercial rates?</p>
          <a
            href="tel:952-931-2577"
            className="text-2xl font-bold text-[#E31837] hover:underline"
          >
            952-931-2577
          </a>
        </div>
      </div>
    </section>
  );
};
